import { useState } from 'react';
import CsvImport from './CsvImport';

const REGION_FLAGS = {
  DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', NL: '🇳🇱', PT: '🇵🇹',
  UK: '🇬🇧', ES: '🇪🇸', SE: '🇸🇪', PL: '🇵🇱',
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', BR: '🇧🇷', CO: '🇨🇴', AR: '🇦🇷',
  AU: '🇦🇺', JP: '🇯🇵', SG: '🇸🇬', AE: '🇦🇪', IN: '🇮🇳',
};

const STATUS = {
  suspended: { bg: 'rgba(248,113,113,0.18)', color: '#f87171', border: 'rgba(248,113,113,0.25)', icon: '🚨', label: 'Suspended', rowBg: 'rgba(220,38,38,0.07)' },
  warning:   { bg: 'rgba(251,191,36,0.18)',  color: '#fbbf24', border: 'rgba(251,191,36,0.25)',  icon: '⚠️', label: 'Warning',   rowBg: 'rgba(245,158,11,0.06)' },
  active:    { bg: 'rgba(52,211,153,0.15)',  color: '#34d399', border: 'rgba(52,211,153,0.2)',   icon: '✓',  label: 'Active',    rowBg: 'rgba(16,185,129,0.04)' },
};

const PAGE_SIZE = 100;

async function downloadCsv(url, filename) {
  const res  = await fetch(url);
  const text = await res.text();
  const blob = new Blob([text], { type: 'text/csv' });
  const href = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = href;
  a.setAttribute('download', filename || 'export.csv');
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

export default function AccountsList({
  accounts,
  accountCounts,
  domainBreakdown = [],
  regionBreakdown = [],
  statusFilter,
  onStatusFilter,
  onImported,
  selectedProvider,
  isMobile,
}) {
  const [domainFilter, setDomainFilter]   = useState('all');
  const [regionFilter, setRegionFilter]   = useState('all');
  const [page, setPage] = useState(0);

  const filtered = accounts.filter(a =>
    (domainFilter === 'all' || a.domain === domainFilter) &&
    (regionFilter === 'all' || a.region === regionFilter)
  );

  const paged   = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pages   = Math.ceil(filtered.length / PAGE_SIZE);

  const totals = accountCounts || { total: 0, active: 0, suspended: 0, warning: 0 };

  return (
    <div>
      <CsvImport onImported={onImported} />

      {/* Top-level status selector + export — wraps rather than widening the page */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, alignItems: 'stretch' }}>
        <StatusButton label="Suspended" count={totals.suspended} color="#dc2626" activeBg="linear-gradient(135deg,#dc2626,#b91c1c)" isMobile={isMobile}
          active={statusFilter === 'suspended'} onClick={() => { onStatusFilter('suspended'); setRegionFilter('all'); setDomainFilter('all'); setPage(0); }} />
        <StatusButton label="Warning" count={totals.warning} color="#d97706" activeBg="linear-gradient(135deg,#d97706,#b45309)" isMobile={isMobile}
          active={statusFilter === 'warning'} onClick={() => { onStatusFilter('warning'); setRegionFilter('all'); setDomainFilter('all'); setPage(0); }} />
        <StatusButton label="Active" count={totals.active} color="#059669" activeBg="linear-gradient(135deg,#059669,#047857)" isMobile={isMobile}
          active={statusFilter === 'active'} onClick={() => { onStatusFilter('active'); setRegionFilter('all'); setDomainFilter('all'); setPage(0); }} />
        <StatusButton label="All accounts" count={totals.total} color="#475569" activeBg="linear-gradient(135deg,#475569,#334155)" isMobile={isMobile}
          active={!statusFilter} onClick={() => { onStatusFilter(''); setRegionFilter('all'); setDomainFilter('all'); setPage(0); }} />

        <div style={{ display: 'flex', gap: 6, flex: isMobile ? '1 1 100%' : '0 0 auto', marginLeft: isMobile ? 0 : 'auto', alignItems: 'stretch' }}>
          <ExportButton
            label="↓ Suspended"
            flex={isMobile}
            color="#dc2626"
            onClick={() => downloadCsv(
              `/api/accounts/export?status=suspended${selectedProvider ? `&provider=${selectedProvider}` : ''}`,
              `suspended_${new Date().toISOString().slice(0,10)}.csv`
            )}
          />
          <ExportButton
            label="↓ Active"
            flex={isMobile}
            color="#059669"
            onClick={() => downloadCsv(
              `/api/accounts/export?status=active${selectedProvider ? `&provider=${selectedProvider}` : ''}`,
              `active_${new Date().toISOString().slice(0,10)}.csv`
            )}
          />
        </div>
      </div>

      {/* Domain tabs */}
      {domainBreakdown.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', paddingBottom: 2 }}>
          <DomainTab label="All" active={domainFilter === 'all'} onClick={() => { setDomainFilter('all'); setPage(0); }} />
          {domainBreakdown.map(d => (
            <DomainTab key={d.domain} label={d.domain}
              count={statusFilter === 'suspended' ? d.suspended : statusFilter === 'active' ? d.active : statusFilter === 'warning' ? d.warning : d.total}
              active={domainFilter === d.domain}
              onClick={() => { setDomainFilter(d.domain); setPage(0); }} />
          ))}
        </div>
      )}

      {/* Region filters */}
      {regionBreakdown.filter(r => r.region !== 'unknown').length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', paddingBottom: 2, alignItems: 'center' }}>
          <RegionChip
            label="All regions"
            active={regionFilter === 'all'}
            onClick={() => { setRegionFilter('all'); setPage(0); }}
          />
          {regionBreakdown.filter(r => r.region !== 'unknown').map(r => {
            const count = statusFilter === 'suspended' ? r.suspended
                        : statusFilter === 'active'    ? r.active
                        : statusFilter === 'warning'   ? r.warning
                        : r.total;
            if (count === 0) return null;
            return (
              <RegionChip
                key={r.region}
                label={`${REGION_FLAGS[r.region] || r.regionFlag || ''} ${r.region}`}
                count={count}
                active={regionFilter === r.region}
                onClick={() => { setRegionFilter(r.region); setPage(0); }}
              />
            );
          })}
        </div>
      )}

      {/* Result count */}
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>
        {filtered.length === 0
          ? 'No accounts match the current filters.'
          : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, filtered.length)} of ${filtered.length.toLocaleString()} accounts`}
      </div>

      {/* Account rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {paged.map((a, i) => {
          const s = STATUS[a.status] || STATUS.active;
          const regionCode = a.region || null;
          const regionFlag = REGION_FLAGS[a.region] || '';
          return (
            <div key={a.accountEmail || i} style={{
              background: s.rowBg,
              border: `1px solid ${s.border}`,
              borderLeft: `3px solid ${s.color}`,
              borderRadius: 8,
              padding: '8px 10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 12, fontFamily: '"SF Mono", "Fira Code", monospace',
                  flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: '#cbd5e1', fontWeight: 450,
                }}>
                  {a.accountEmail}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                  background: s.bg, color: s.color, flexShrink: 0,
                }}>
                  {s.icon} {s.label}
                </span>
              </div>
              {(regionCode || a.latestEvent?.emailDate) && (
                <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  {regionCode && (
                    <span style={{
                      fontSize: 11, color: '#94a3b8',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 5, padding: '1px 6px',
                    }}>
                      {regionFlag} {regionCode}
                    </span>
                  )}
                  {a.latestEvent?.emailDate && (
                    <span style={{ fontSize: 11, color: '#475569' }}>
                      {new Date(a.latestEvent.emailDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 14, justifyContent: 'center', alignItems: 'center' }}>
          <PageBtn label="← Prev" disabled={page === 0}        onClick={() => setPage(p => p - 1)} />
          <span style={{ fontSize: 12, color: '#888' }}>Page {page + 1} of {pages}</span>
          <PageBtn label="Next →" disabled={page >= pages - 1}  onClick={() => setPage(p => p + 1)} />
        </div>
      )}
    </div>
  );
}

function StatusButton({ label, count, color, activeBg, active, onClick, isMobile }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-start', gap: 9,
      flex: isMobile ? '1 1 calc(50% - 4px)' : '0 1 auto',
      background: active ? activeBg || color : 'rgba(255,255,255,0.04)',
      color: active ? '#fff' : color,
      border: `1px solid ${active ? 'transparent' : color + '55'}`,
      borderRadius: 10, padding: '10px 16px',
      cursor: 'pointer', fontWeight: active ? 600 : 500,
      boxShadow: active ? '0 2px 12px rgba(0,0,0,0.3)' : 'none',
      transition: 'all 0.15s',
      minWidth: 80,
    }}>
      <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{count?.toLocaleString()}</span>
      <span style={{ fontSize: 11, opacity: active ? 0.9 : 0.75, fontWeight: active ? 600 : 500 }}>{label}</span>
    </button>
  );
}

function DomainTab({ label, count, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: active ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
      color: active ? '#f1f5f9' : '#64748b',
      border: `1px solid ${active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)'}`,
      borderRadius: 8, padding: '5px 13px', fontSize: 12,
      fontWeight: active ? 600 : 450,
      cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 7,
      transition: 'all 0.12s',
    }}>
      {label}
      {count !== undefined && (
        <span style={{
          fontSize: 11,
          background: active ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.06)',
          color: active ? '#f1f5f9' : '#475569',
          borderRadius: 10, padding: '1px 7px',
        }}>
          {count?.toLocaleString()}
        </span>
      )}
    </button>
  );
}

function RegionChip({ label, count, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 5,
      background: active ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.04)',
      color: active ? '#fff' : '#64748b',
      border: `1px solid ${active ? 'transparent' : 'rgba(255,255,255,0.08)'}`,
      borderRadius: 20, padding: '4px 12px', fontSize: 12,
      cursor: 'pointer', fontWeight: active ? 600 : 450,
      transition: 'all 0.12s',
      boxShadow: active ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
    }}>
      {label}
      {count !== undefined && (
        <span style={{
          fontSize: 11,
          background: active ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.07)',
          borderRadius: 10, padding: '0 6px',
          color: active ? '#fff' : '#475569',
        }}>
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}

function ExportButton({ label, color, flex, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
      flex: flex ? 1 : undefined,
      background: 'rgba(255,255,255,0.04)',
      color,
      border: `1px solid ${color}55`,
      borderRadius: 8, padding: '8px 13px',
      fontSize: 12, fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.12s',
    }}>
      {label}
    </button>
  );
}

function PageBtn({ label, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: 'rgba(255,255,255,0.04)',
      color: disabled ? '#334155' : '#94a3b8',
      border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8,
      padding: '6px 14px', fontSize: 12,
      fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.12s',
    }}>
      {label}
    </button>
  );
}
